import { Schedule } from './store-schedule/stores-schedule';
import { StoreSubscription } from './store-subscriptions/subscriptions';
import { Tenant } from "../store-platform/platform";
import {ReferenceType} from "../public/store/types/IntegrationPayment";
import {Currency} from "../api/types/Currency";

export interface ClientStore {
  id: number;
  name: string;
  companyName: string;
  vatNumber: string;
  description: string;
  coordinates: {
    longitude: number;
    latitude: number;
  };
  aliasName: string;
  address: StoreAddress;
  companyAddress: StoreCompanyAddress;
  phoneNumber: string;
  language: {
    id: number;
    name: string;
    locale: string;
  };
  settings: { [key: string]: any };
  externalId: string;
  numberOfLocations: number;
  numberOfOffers: number;
  numberOfOrders: number;
  subscription?: StoreSubscription;
  currency?: {
    name: string;
    isoCode: string;
    iso4217Code: string;
    symbol: string;
    id: number;
  };
  timeZone?: string;
  orderMinAmountDelivery?: number;
  orderFeeDelivery?: number;
  orderAmountFreeDelivery?: number;
  specialSchedules: SpecialSchedule[];
  tag: string;
  createdAt: string;
  vatPercentage?: number;
  relation: Relation;
  isIndependent?: boolean;
  locationUrl?: string;
  openTimeSchedule?: {};
  platformOrderFee?: number;
  platformOrderPercentageFee?: number;
  platformPaymentFee?: number;
  platformPaymentPercentageFee?: number;
  subscriptionPrice1Month?: number;
  subscriptionPrice1Year?: number;
  crmCompanyId?: number;
  tenant?: Tenant
  firstLoginVoucherId?: number;
  locale?: string;
  providerConnectUrl?: string;
  selectedCurrency?: {
    name: string;
    isoCode: string;
    iso4217Code: string;
    symbol: string;
    id: number;
    locale?: string;
  };
}

export interface StoreAddress {
  addressLine1: string;
  addressLine2: string;
  postCode: string;
  region: string;
  city: string;
  country: {
    id: number;
    name: string;
    code?: string;
    phoneCode: string;
    defaultLocale?: string;
    europeanCountry: boolean;
    subscriptionCurrency?: string;
  };
}
export interface StoreCompanyAddress {
  addressLine1: string;
  addressLine2: string;
  postCode: string;
  region: string;
  city: string;
  country: {
    id: number;
    name: string;
    code?: string;
    phoneCode: string;
    defaultLocale?: string;
    europeanCountry: boolean;
  };
}

export interface ClientStoreRequest {
  id?: number;
  name?: string;
  description?: string;
  aliasName?: string;
  externalId?: string;
  longitude?: number;
  latitude?: number;
  countryId?: number;
  languageId?: number;
  addressLine1?: string;
  addressLine2?: string;
  postCode?: string;
  region?: string;
  city?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  timeZone?: string;
  orderMinAmountDelivery?: number;
  orderFeeDelivery?: number;
  subscription?: StoreSubscription;
  crmCompanyId?: string;
  firstLoginVoucherId?: number | null;
  tenantCode?: string;
  settings?: { [key: string]: any };
  selectedCurrency?: {
    name: string;
    isoCode: string;
    iso4217Code: string;
    symbol: string;
    id: number;
  };
  currency?: {
    name: string;
    isoCode: string;
    iso4217Code: string;
    symbol: string;
    id: number;
  };
}

export interface StoreSettingUpdateRequest {
  key: string;
  value: any;
}

export interface QRCodeResponse {
  blob: Blob;
  filename: string;
}

export interface OrderItemXlsResponse {
  blob: Blob;
  filename: string;
}

export interface CategoriesListResponse {
  categories: Category[];
}

export interface Category {
  categoryId: number;
  name: string;
  shortDescription?: string;
  longDescription?: string;
  availability: boolean;
  hierarchyLevel?: string;
  isSellable: boolean;
  languageTranslation?: LanguageTranslation[];
  max?: number;
  min: number;
  offers: Offer[];
  position: number;
  catalogId: number;
  orderMultipleSameItem?: boolean;
  topLevelCategoryId?: number;
  standardImage?: string;
}

export interface StoreCatalog {
  categories: Category[];
  topLevelCategories?: Category[];
  offers: Offer[];
  catalogId: number;
  currency: string;
  parentStoreId?: number;
}

export interface SaveOfferView {
  offer: Offer;
  contentItem: ContentItemModel[];
}

export interface User {
  id: number;
  email: string;
  username: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  phoneCode: string;
}

export interface SaveCategoryView {
  category: Category;
  contentItem: ContentItemModel[];
}

export interface SaveOfferPositionView {
  offers: Offer[];
}
export interface SaveCategoryRequest {
  category: Category;
  contentItem: ContentItemModel[];
}

export interface Offer {
  offerId: number;
  name: string;
  shortDescription?: string;
  longDescription?: string;
  price: number;
  discount: number;
  categoryId: number;
  position: number;
  availability: number;
  contentItem?: ContentItemModel[];
  discountType: string;
  aliasName?: string;
  isSellable: boolean;
  global?: boolean;
  catalogId: number;
  standardImage?: ImageData;  // needed to be (at least optionally) defined for public interface
  options?: any[];    // needed to be (at least optionally) defined for public interface
  categories?: Category[];
  contentItemId?: number;
  hierarchyLevel?: string;
  languageTranslation?: LanguageTranslation[];
  variants?: OfferVariant[];
  priceDescription?: string;
  isOrderable: boolean;
  attributeDtos: KeyValuePair[];
  isDirectlyOrderable?: boolean;
  isExpandable?: boolean;
  inStock?: boolean;
  originalOffer?: Offer;
  consumption?: number;
}

export interface OfferVariant {
  offerId: number;
  price: number;
  priceDescription?: string;
  discount?: number;
  discountType?: string;
  categories?: Category[];
  consumption?: number;
}

export interface ContentItemModel {
  languageId: number;
  name: string;
  shortDescription: string;
  longDescription: string;
}

export interface OrderItem {
  childItemRequests?: any[];
  offerId: number;
  name?: string;
  hierarchyLevel?: string;
  variantOfferId?: number;
  priceDescription?: string;
  itemName?: string;
  quantity: number;
  discountType?: string;
  discountValue?: number;
  offerPrice?: number;
  discountedOfferPrice?: number;
  price?: number;
  totalNonDiscountedPrice?: number;
  totalDiscountedPrice?: number;
  uuid?: string;
  comment?: string;
  formattedDiscountValue?: string;
  formattedOfferPrice?: string;
  formattedTotalNonDiscountedPrice?: string;
  formattedTotalDiscountedPrice?: string;
  childOrderItems?: OrderItem[];
  wishTime?: string;
}

export interface Order {
  locationId?: number;
  location?: string | number;
  locationComment?: string;
  orderItems?: OrderItem[];
  status?: 'DRAFT' | 'SUBMITTED' | 'RECEIVED' | 'CLOSED' | 'CANCELLED' | 'REJECTED';
  uuid?: string;
  totalDiscountedPrice?: number;
  totalNonDiscountedPrice?: number;
  deliveryFee?: number;
  transactionFee?: number;
  formattedTotalDiscountedPrice?: string;
  formattedTotalNonDiscountedPrice?: string;
  formattedDeliveryFee?: string;
  formattedTransactionFee?: string;
  formattedServiceChargeAmount?: string;
  comment?: string;
  customerUserId?: number;
  customerName?: string;
  customerPhoneNumber?: string;
  customerEmail?: string;
  buzzerNumber?: string;
  deliveryMethod?: 'NO_LOCATION' | 'IN_STORE_LOCATION' | 'ADDRESS';
  paymentStatus?: 'NO_PAYMENT' | 'IN_PROGRESS' | 'SUCCESSFULLY_COMPLETED' | 'FAILED' | 'PAID_OFFLINE' | 'PAID_CARD';
  currency?: string;
  orderToken?: string;
  createdAt?: string;
  wishTime?: string;
  slotEndTime?: string;
  estimatedDuration?: Date;
  isReady?: boolean;
  deliveryStreetAddress?: string;
  deliveryPostCode?: string;
  deliveryCity?: string;
  deliveryFeeApply?: boolean;
  floorNumber?: string;
  doorbell?: string;
  estimatedTime?: string;
  rejectReason?: string;
  uiLanguageLocale?: string;
  catalogLanguageLocale?: string;
  voucherCode?: string;
  voucherDiscount?: number;
  voucherDiscountType?: 'MONETARY' | 'PERCENTILE';
  voucherDiscountPercentage?: number;
  formattedVoucherDiscount?: string;
  latitude?: number;
  longitude?: number;
  storeId?: number;
  storeAliasName?: string;
  storeUserFirstName?: string;
  storeUserLastName?: string;
  paymentMethod?: string;
  tapId?: number;
  external_address_delivery_id?: number;
  externalOrderId?: string;
  submittedAt?: string;
  deliveryComment?: string;
  currencyIsoCode?: string;
  originType?: string;
  originDomain?: string;
  serviceChargeAmount?: number;
  serviceChargePercentage?: number;
  billingStreetAddress?: string;
  billingHouseNo?: string;
  billingPostCode?: string;
  billingCity?: string;
  billingState?: string;
  billingCountryId?: number;
  billingCountryName?: string;
  posTechnicalIssueFlag?: boolean;
  preferredMode?: string;
  returnDate?: string;
  paymentTransaction?: PaymentTransaction;
  tip?: number;
  formattedTip?: string;
}

export interface PaymentTransaction {
  id?: string;
  paymentMethod?: string;
  paymentProviderIdentifier?: string;
  amount?: number;
  currency?: Currency;
  referenceType?: ReferenceType;
  paymentStatus?: 'NO_PAYMENT' | 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'FAILED' | 'ERROR' | 'PAID_OFFLINE' | 'PAID_CARD';
  paymentParams?: any;
}


export interface StoreViewState {
  state:
  'LOADING' |
  'LOADED' |
  'VIEWPRODUCTDETAILS' |
  'VIEWPRODUCTDETAILSFROMCARTVIEW' |
  'LOGIN' |
  'PROFILE' |
  'EDITPROFILE' |
  'EDITADDRESS' |
  'VIEWCART' |
  'CHECKOUT' |
  'THANKYOUPAGE' |
  'ERRORPAGE' |
  'EMPTYCARTPAGE' |
  'STANDALONEPAYMENT' |
  'PAYMENTPROGRESS' |
  'PAYMENTPAY' |
  'QRCODEPAY'
  ;
}

export interface OrderMeta {
  comment?: string;
  customerUserId?: number;
  customerEmail?: string;
  customerName?: string;
  customerPhoneNumber?: string;
  customerStreet?: string;
  customerZip?: string;
  customerTown?: string;
  deliveryMethod?: string;
  paymentOption?: number;
  paymentMethod?: number;
  paymentStatus?: string;
  location?: string;
  voucherCode?: string;
  wishTime?: string;
  customerFloor?: string;
  doorbell?: string;
  latitude?: number;
  longitude?: number;
  tapId?: string;
  deliveryComment?: string;
  billingStreetAddress?: string;
  billingHouseNo?: string;
  billingPostCode?: string;
  billingCity?: string;
  billingState?: string;
  billingCountryId?: number;
  buzzerNumber?: number;
  selectedAddressId?: number;
  tip?: number;
}

export interface Availability {
  id: number;
  startTime: string;
  endTime: string;
}

export interface Lang {
  id: number;
  locale: string;
  name: string;
}

export interface LanguageTranslation {
  languageId: number;
  name: string;
  shortDescription?: string;
  longDescription?: string;
}

export interface Voucher {
  id: number;
  code: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  maxTimesUse: number;
  value: number;
  discount: number;
  owner: number;
  commission: number;
  comment: string;
  voucherAssignmentStatus: string;
  formattedDiscountAmount: string;
  formattedCommissionAmount: string;
}

export interface Commission {
  code: string;
  storeId: number;
  storeAlias: string;
  commission: number;
  commissionAmount: number;
  formattedCommissionAmount: string;
  useDate: string;
  formattedEndDate: string;
  commissionStatus: string;
}

export interface StoreVoucherRequest {
  id: number;
  total: number;
  discount: number;
  commission: number;
  comment: string;
  mode: string;
  code: string;
  endDate: any;
  voucherAssignmentStatus: string;
  formattedDiscountAmount: string;
  formattedCommissionAmount: string;
}

export interface StoreStatistics {
  day: number;
  year: number;
  month: number;
  draftCount: number;
  submitCount: number;
  receivedCount: number;
  cancelCount: number;
  closedCount: number;
  totalSubmitted: number;
  totalViewings: number;
  totalPaid: number;
  totalNonPaid: number;
  total: number;
  totalTips: number;
}

export interface StoreOrderItemsStatistics {
  offer: string;
  totalQuantity: number;
  totalNonDiscountedPrice: number;
  totalDiscountedPrice: number;
  imageUrl: string;
}

export enum LocationType {
  TABLE = 'TABLE',
  ROOM = 'ROOM',
  LOCATION = 'LOCATION',
  POLE = 'POLE',
  SEAT = 'SEAT',
  SUNBED = 'SUNBED',
  UMBRELLA = 'UMBRELLA',
  SOFA = 'SOFA',
  AIRBNB = 'AIRBNB',
}

export interface LocationValid {
  isValid: boolean;
  id?: number;
  label?: string;
  storeLocations?: StoreLocation[];
  locationType?: LocationType;
  description?: string;
  status?: string;
  deliveryFeeApply?: boolean;
  openTab?: boolean
}

export interface StoreLocation {
  id: number;
  label: string;
}

export interface TimeZone {
  zoneId: string;
  label: string;
}

export interface EmailMessage {
  body: {};
  statusCode: string;
  statusCodeValue: number;
}

export interface SendEmailModel {
  storeId: number;
  userId: number;
  message: string;
}

export interface StoreZone {
  id?: number;
  name: string;
  type: string;
  minRadius?: number;
  maxRadius?: number;
  radiusUnit: string;
  orderMinAmountDelivery?: number;
  orderFeeDelivery?: number;
  orderAmountFreeDelivery?: number;
  postcodes: string;
  settings: { [key: string]: any };
  scheduleId: number;
  position?: number;
}

export interface SaveZonePositionView {
  zones: StoreZone[];
}


export interface StoreZoneStatus {
  enableRestriction: boolean;
}

export type SpecialScheduleType = 'OPENING_HOURS';

export interface SpecialSchedule {
  id: number;
  type: SpecialScheduleType;
  schedule: Schedule;
}
export interface KeyValuePair {
  key: string;
  value: boolean;
}

export class MynextLoginResponse {
  ApiKey: string;
  MyNextId: string;
  Message: string;
}
export class PowersoftLoginResponse {
  accessToken: string;
  agentCode: string;
  genericCustomerCode: string;
  genericCustomerEmail: string;
}

export class PowersoftLoginRequest {
  accessToken: string;
  agentCode: string;
  genericCustomerCode: string;
  genericCustomerEmail: string;
}

export class WoltLoginRequest {
  venueId: string;
  username: string;
  password: string;
}

export interface Customer {
  id: any;
  name: string;
  email: string;
  phoneNumber: string;
  orderCount: number;
  firstOrderUpdatedAt: string;
  lastOrderUpdatedAt: string;
  reservationCount?: any;
  customerStatus?: string;
  loyalty: boolean,
  identified: boolean,
  registered: boolean,
}
export interface CustomerBasic {
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  orderCount?: number;
  streetAddress?: string;
  city?: string;
  zipCode?:string;
  customerStatus?:any;
  customerGender?: string;
  customerBirthday?: Date;
  customerPreferences?: any;
  instagramUsername?: string;
}

export interface CustomersListDownloadResponse {
  blob: Blob;
  filename: string;
}

export interface AssociatedZone {
  id: number;
  name: string;
  type: string;
  orderMinAmountDelivery: number;
  orderFeeDelivery: number;
  orderAmountFreeDelivery: number;
  postcodes: string;
  settings: { [key: string]: any };
  storeId: number;
}
export interface StoreRelations {
  isIndependent: boolean;
  storeAlias: string;
  storeId: number;
  storeName: string;
}

export interface Relation {
  childStores: StoreRelations[];
  parentStore: StoreRelations;
  siblingStores: StoreRelations[];
  isIndependent?: boolean;
}

export interface StoreRelationRequest {
  isIndependent: boolean;
  parentStoreId: number;
}

export interface CustomEndPointValidationRequest {
  endpoint: string;
}

export interface PresetOrderData {
  comment?: string;
  customerEmail?: string;
  customerName?: string;
  customerPhoneNumber?: string;
  deliveryStreetAddress?: string;
  deliveryPostCode?: string;
  deliveryCity?: string;
  deliveryComment?: string;
  floorNumber?: string;
  doorbell?: string;
  latitude?: number;
  longitude?: number;
  externalOrderId?: string;
}
export enum StoreSaveMode {
  QRSCAN = "QRSCAN",
  URL = "URL",
  DEEPLINK = "DEEPLINK",
}

export enum EmailStatus {
  UNVERIFIED = "Unverified",
  VERIFIED = "Verified",
  BOUNCED = "Bounced"
}

export interface StoreRecentsInfo {
  storeId: number;
  storeAlias: string;
  storeName: string;
  storeUrl: string;
  saveMode: StoreSaveMode;
  storeLogoUrl: string;
  lastOpen: string;
}
export interface CustomerLoyalty {
  balance: number;
  voucherValue: number;
  voucherCode: string;
  createdAt: string;
  updatedAt: string;
}
export interface CustomerForm {
  email?: string;
  firstName: string;
  lastName: string;
  countryId?: number;
  phoneNumber?: string;
  languageId?:number;
  memberCode?:string;
}
export interface CustomerDetail {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  emailVerified: EmailStatus;
  phoneNumber: string;
  loginAt: string;
  memberCode?:string;
  country: {
    id: number,
    name: string,
    code: string,
    phoneCode: string,
    defaultLocale: string,
    defaultTimeZone: string,
    subscriptionPrice: number,
    subscriptionCurrency: string,
    europeanCountry: boolean,
  };
  preferredLanguage: {
    id: number,
    name: string,
    locale: string,
    code: string,
    covered_admin_ui: boolean,
    covered_customer_ui: boolean
  };
  googleAccount?:{
    appleIdentifier?:string,
    email?:string,
    firstName?:string,
    lastName?:string,
    picture?: string
  };
  facebookAccount?:{
    appleIdentifier?:string,
    email?:string,
    firstName?:string,
    lastName?:string,
    picture?:string
  };
  appleAccount?:{
    appleIdentifier?:string,
    email?:string,
    firstName?:string,
    lastName?:string,
    picture?:string
  };
  storeRole:string;
  roles:Array<string>;
  registeredAt?:string;
  instagramUsername?: string;
}

export interface CustomerOrderSummary {
  count:number;
  firstUpdated:string;
  lastUpdated:string;
}

export interface CustomerReservationStatistics {
  count:number;
  firstUpdated:string;
  lastUpdated:string;
}

export interface CustomerEventBookingsStatistics {
  bookingCount:number;
  firstBookingDate:string;
  lastBookingDate:string;
  ticketCount:number;
  reservationCount:number;
  totalSpent:number;
}

export interface CustomerOrderStatistics {
  registeredAt:string;
  lastLogindAt:string;
  accountType:Array<any>
}

export interface CustomerLoyaltyProfile {
  balance?:number;
  voucherValue?:number;
  totalDiscountedAmountSpent?: number;
  totalNonDiscountedAmountSpent?: number;
  totalRewardCollected?: number;
  firstRegisteredStoreId?: number;
  amountCollected?: number;
  totalCollected?:number;
  totalRedeemed?:number;
  amountRedeemed?: number;
  voucherCode?:string;
  lastTransaction?:any;
  sinceMemebership?:any,
  updatedAt?:any,
  createdAt?:any
}
export interface CustomerLoyaltyProfileUpdate {
  balance?: number;
  voucherValue?: number;
  totalNonDiscountedAmountSpent?: number;
  totalCollected?: number;
  totalRedeemed?: number;
}

export interface CustomerLoyaltyProfileUpdateData {
  amountToRedeem: number;
  amountCollected: number;
}

export interface CustomerOrderItem {
  offerId:number;
  offer:string;
  totalQuantity:number;
  totalDiscountedPrice:number;
  imageUrl?:string;
}
export interface CustomerRecentOrders {
  content:Array<any>;
}
export interface CustomerOrder {
  orderId?: number;
  uuid?: string;
  orderToken?: string;
  deliveryMethod?: string;
  customerName?: string;
  amountPaidOnline?: number;
  wishTime?: string;
  createdAt?: string;
  submittedAt?: string;
  updatedAt?: string;
  estimatedTime?: string;
}

export interface CustomerOrderItems {
  data:Array<CustomerOrderItem>;
}

export interface CustomerLoyaltyTrasaction {
  orderToken: string;
  nonDiscountedAmountSpent: number;
  initialBalance: number;
  finalBalance: number;
  initialReward: number;
  amountCollected: number;
  amountRedeemed: number;
  finalReward: number;
  createdAt: string;
  storeId: number;
  uuid: string;
}

export interface CustomerLoyaltyTrasactions {
  content:Array<CustomerLoyaltyTrasaction>;
}

export interface CustomerReservation {
  createdAt:string;
  updatedAt:string;
  id:number;
  uuid:string;
  storeId:number;
  reservationNumber:number;
  arrivalTime:string;
  departureTime:string;
  numberOfPeople:number;
  name:string;
  phone:string;
  email:string;
  status:string;
  comment:string;
  occasion:string;
  reason:string;
  table:string;
}

export interface UploadResponse {
  downloadUrl: string;
}

export enum StoreIntegrationType {
  PAYMENT_PROVIDERS,
  POS
}

export enum StoreIntegrationsPosTypes {
  MYNEXT = 'mynext',
  FLEXDELIVERY = 'flexdelivery',
  INSTADELIVERY = 'instadelivery',
  SUNSOFT = 'sunsoft',
  TWINSOFT = 'twinsoft',
  HIT = 'hit',
  POWERSOFT = 'powersoft',
  EPOSNOW = 'eposnow',
  HUBRISE = 'hubrise',
  SHOPIFY = 'shopify',
  POSTER = 'poster',
  EPOSDIRECT = 'eposdirect',
  CUSTOM = 'custom',
  WOLT = 'wolt',
  IOTSOFT = 'iotsoft',
  WITEC = 'witec'
}

export enum StoreIntegrationsPaymentProvidersTypes {
  PAYABL = 'payabl',
  MOLLIE = 'mollie',
  JCC = 'jcc',
  STRIPE = 'stripe',
  TRUST = 'trust',
  GLOBALPAY = 'globalpay',
  RMS = 'rms',
  PAYMENTSENSE = 'paymentsense',
  VIVA = 'viva',
  EPAY = 'epay',
  SQUARE = 'square',
  FYGARO = 'fygaro',
  APCOPAY = 'apcopay',
  HOBEX = 'hobex',
  RYFT = 'ryft',
  ABAPAYWAY = 'abapayway',
  ROOTLINE = 'rootline',
  SOLIDGATE = 'solidgate',
  EMERCHANTPAY = 'emerchantpay',
  OTHER = 'other',
  PAYPAL = 'paypal',
  NEOSOFT = 'neosoft',
  FLITT = 'flitt'
}
